import { useState, useRef, useCallback, ChangeEvent, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadCompanyLogo, RemoveCompanyLogo } from '../../../../../api/Payroll/companyLogo';
import { useFileUploadLimits } from '../../../../../hooks/useFileUploadLimits';
import type { BrandingSettings } from '../types';

export interface UseLogoUploadReturn {
  dragActive: boolean;
  uploadingLogo: boolean;
  uploadProgress: number;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleDrop: (e: DragEvent<HTMLDivElement>) => Promise<void>;
  handleDragOver: (e: DragEvent<HTMLDivElement>) => void;
  handleDragEnter: (e: DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  handleFileInput: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleRemoveLogo: () => void;
  getMaxLogoSizeMB: () => number;
}

export function useLogoUpload(
  setBrandingSettings: React.Dispatch<React.SetStateAction<BrandingSettings>>,
  onSettingsChange: (() => void) | undefined,
  showToast: (message: string, type: 'success' | 'error' | 'info') => void,
): UseLogoUploadReturn {
  const { t } = useTranslation('payroll');
  const { validateFileSize, getMaxSizeMB } = useFileUploadLimits();
  const [dragActive, setDragActive] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadLogoToServer = useCallback(async (file: File): Promise<string | null> => {
    setUploadingLogo(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await UploadCompanyLogo(formData, (evt: ProgressEvent) => {
        if (evt.lengthComputable) {
          const percent = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(percent);
        }
      });

      if (response && response.status === 200 && response.payload) {
        return response.payload.logoUrl;
      }
      return null;
    } catch (error) {
      console.error('Logo upload failed:', error);
      return null;
    } finally {
      setUploadingLogo(false);
      setUploadProgress(0);
    }
  }, []);

  const handleLogoUpload = useCallback(
    async (file: File): Promise<void> => {
      if (file && file.type.startsWith('image/')) {
        const { valid, maxMB } = validateFileSize(file, 'payslipBrandingLogoMaxSizeMB');
        if (!valid) {
          showToast(t('fileSizeExceedsLimit', { name: file.name, maxMB }), 'error');
          return;
        }

        const uploadedUrl = await uploadLogoToServer(file);

        if (uploadedUrl) {
          setBrandingSettings((prev) => ({
            ...prev,
            logoSettings: {
              ...prev.logoSettings,
              hasLogo: true,
              logoUrl: uploadedUrl,
              logoFile: file,
            },
          }));
          onSettingsChange?.();
          showToast(t('payslipBranding.logoUploadSuccess'), 'success');
        } else {
          showToast(t('payslipBranding.logoUploadFailed'), 'error');
        }
      } else {
        showToast(t('payslipBranding.invalidImageFormat'), 'error');
      }
    },
    [validateFileSize, uploadLogoToServer, setBrandingSettings, onSettingsChange, showToast, t],
  );

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>): Promise<void> => {
      e.preventDefault();
      setDragActive(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        await handleLogoUpload(files[0]);
      }
    },
    [handleLogoUpload],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleFileInput = useCallback(
    async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0];
      if (file) {
        await handleLogoUpload(file);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [handleLogoUpload],
  );

  const handleRemoveLogo = useCallback((): void => {
    (async () => {
      try {
        setUploadingLogo(true);
        await RemoveCompanyLogo();
        setBrandingSettings((prev) => ({
          ...prev,
          logoSettings: {
            ...prev.logoSettings,
            hasLogo: false,
            logoUrl: null,
            logoFile: null,
          },
        }));
        onSettingsChange?.();
        showToast(t('payslipBranding.logoRemoved'), 'info');
      } catch (err) {
        console.error('Failed to remove logo', err);
        showToast(t('payslipBranding.logoRemoveFailed'), 'error');
      } finally {
        setUploadingLogo(false);
      }
    })();
  }, [setBrandingSettings, onSettingsChange, showToast, t]);

  const getMaxLogoSizeMB = useCallback((): number => {
    return getMaxSizeMB('payslipBrandingLogoMaxSizeMB') || 2;
  }, [getMaxSizeMB]);

  return {
    dragActive,
    uploadingLogo,
    uploadProgress,
    fileInputRef,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleFileInput,
    handleRemoveLogo,
    getMaxLogoSizeMB,
  };
}
